import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import {
  Grid,
  TextField,
  InputAdornment,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { useApiGet, useApiSend } from '../../../utils/httpClientTs';
import {
  getReciprocityList,
  createReciprocity,
  checkFinancialPermission,
} from '../../../endpoints/reciprocity';
import ServerSideTable, {
  ServerSideTableColumn,
} from '../../../Components/ServerSideTable';
import ReciprocityType from '../../../Components/ReciprocityTypes';
import SPDSelector from '../../../Components/SPDSelector';
import {
  ReciprocityMinimal,
  ReciprocityCreate,
} from '../../../types/reciprocity';
import { SortOrder } from '../../../types/table';
import { reciprocityTypes, roles } from '../../../utils/variables';
import AlertItem from '../../../Components/AlertItem';

const columns: ServerSideTableColumn<ReciprocityMinimal>[] = [
  {
    id: 'name',
    label: 'Name',
    width: '50%',
    firstCell: true,
    sortable: true,
  },
  {
    id: 'reciprocity_type',
    label: 'Reciprocity Type',
    width: '30%',
    firstCell: true,
    sortable: true,
  },
  {
    id: 'is_active',
    label: 'Active?',
    width: '20%',
    sortable: true,
    render: (value: boolean) => (
      <Typography variant="body2" color={value ? 'success.main' : 'error.main'}>
        {value ? 'Yes' : 'No'}
      </Typography>
    ),
  },
];

export default function ReciprocityList(): React.JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const updateSearchParams = useCallback(
    (key: string, value: string) => {
      if (!value || value === '') {
        searchParams.delete(key);
      } else {
        searchParams.set(key, value);
      }
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams]
  );

  const [order, setOrder] = useState<SortOrder>('asc');
  const [orderBy, setOrderBy] = useState<keyof ReciprocityMinimal>('name');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [delaySearch, setDelaySearch] = useState(
    searchParams.get('search') || ''
  );
  const [reciprocityType, setReciprocityType] = useState(
    searchParams.get('reciprocityType') || 'all'
  );
  const [showCreateDialog, setShowCreateDialog] = useState<boolean>(false);
  const [createData, setCreateData] = useState<Partial<ReciprocityCreate>>({
    name: '',
    reciprocity_type_id: '',
    spd_chapter_id: '',
    is_active: true,
  });
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [canEditFinancial, setCanEditFinancial] = useState<boolean>(false);

  // Check financial permission
  const { data: financialPermission } = useApiGet<{
    can_edit_financial: boolean;
  }>(['reciprocity-financial-permission'], checkFinancialPermission, {
    enabled: true,
    retry: 1,
  });

  useEffect(() => {
    if (financialPermission) {
      setCanEditFinancial(financialPermission.can_edit_financial);
    }
  }, [financialPermission]);

  // Update search params when search changes
  useEffect(() => {
    updateSearchParams('search', search);
  }, [search, updateSearchParams]);

  useEffect(() => {
    updateSearchParams('reciprocityType', reciprocityType);
  }, [reciprocityType, updateSearchParams]);

  const { data, isFetching, refetch } = useApiGet(
    [
      'reciprocities',
      rowsPerPage,
      page,
      orderBy,
      order,
      search,
      reciprocityType,
    ],
    getReciprocityList,
    {
      enabled: true,
      retry: 1,
    },
    {
      rowsPerPage,
      page,
      orderBy,
      order,
      search,
      reciprocityType,
    }
  );

  const { mutate: createReciprocityMutation, isPending: isCreating } =
    useApiSend(
      (data: ReciprocityCreate) => createReciprocity(data),
      () => {
        setShowCreateDialog(false);
        setCreateData({
          name: '',
          reciprocity_type_id: '',
          spd_chapter_id: '',
          is_active: true,
          financial_name: undefined,
          tax_id_number: undefined,
        });
        setSuccessMessage('Reciprocity created successfully!');
        refetch();
        setTimeout(() => setSuccessMessage(''), 3000);
      },
      (error: Error) => {
        setErrorMessage('Failed to create reciprocity. Please try again.');
        console.error('Error creating reciprocity:', error);
      }
    );

  const canCreateReciprocity = () => {
    const userRole = localStorage.getItem('role');
    return userRole === roles.Admin || userRole === roles.Support;
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearch(delaySearch);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [delaySearch]);

  const handleRowClick = (row: ReciprocityMinimal) => {
    navigate(`/reciprocity/${row.id}`);
  };

  const handleReciprocityTypeChange = (value: string) => {
    setReciprocityType(value);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0); // Reset to first page when changing rows per page
  };

  const handleSort = (property: keyof ReciprocityMinimal) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleCreateReciprocity = () => {
    if (
      createData.name &&
      createData.reciprocity_type_id &&
      createData.spd_chapter_id
    ) {
      const createPayload: ReciprocityCreate = {
        name: createData.name,
        reciprocity_type_id: createData.reciprocity_type_id,
        spd_chapter_id: createData.spd_chapter_id,
        is_active: createData.is_active || true,
      };

      // Only include financial fields if user has permission
      if (canEditFinancial) {
        if (createData.financial_name) {
          createPayload.financial_name = createData.financial_name;
        }
        if (createData.tax_id_number) {
          createPayload.tax_id_number = createData.tax_id_number;
        }
      }

      createReciprocityMutation(createPayload);
    }
  };

  return (
    <Grid container>
      <Grid item xs={12} paddingBottom={5} textAlign={'center'}>
        <Typography variant="h4" color={'primary'}>
          Reciprocities
        </Typography>
      </Grid>

      {/* Create Button */}
      {canCreateReciprocity() && (
        <Grid item xs={12} sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              color="success"
              startIcon={<Add />}
              onClick={() => setShowCreateDialog(true)}
              disabled={isCreating}
            >
              Create Reciprocity
            </Button>
          </Box>
        </Grid>
      )}

      {/* Messages */}
      {errorMessage && (
        <Grid item xs={12} sx={{ mb: 2 }}>
          <AlertItem
            severity="error"
            description={errorMessage}
            onClose={() => setErrorMessage('')}
          />
        </Grid>
      )}
      {successMessage && (
        <Grid item xs={12} sx={{ mb: 2 }}>
          <AlertItem
            severity="success"
            description={successMessage}
            onClose={() => setSuccessMessage('')}
          />
        </Grid>
      )}
      <Grid container paddingLeft={2} paddingRight={2}>
        <Grid item xs={12} sm={6} paddingRight={2} paddingBottom={3}>
          <TextField
            label="Search"
            id="search-field"
            size="small"
            value={delaySearch}
            fullWidth={true}
            onChange={(event) => setDelaySearch(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} sm={3} paddingRight={2}>
          <ReciprocityType
            value={reciprocityType}
            onUpdate={(value) => {
              handleReciprocityTypeChange(value);
            }}
          />
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <ServerSideTable
          data={data?.data || []}
          columns={columns}
          loading={isFetching}
          page={page}
          rowsPerPage={rowsPerPage}
          totalCount={data?.pagination?.total_count || 0}
          totalPages={data?.pagination?.total_pages || 0}
          hasNext={data?.pagination?.has_next || false}
          hasPrev={data?.pagination?.has_prev || false}
          order={order}
          orderBy={orderBy}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          onSort={handleSort}
          onRowClick={handleRowClick}
          emptyMessage="No reciprocities found"
        />
      </Grid>

      {/* Create Reciprocity Dialog */}
      <Dialog
        open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New Reciprocity</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Name"
              value={createData.name || ''}
              onChange={(e) =>
                setCreateData({ ...createData, name: e.target.value })
              }
              required
            />
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={createData.reciprocity_type_id || ''}
                onChange={(e) =>
                  setCreateData({
                    ...createData,
                    reciprocity_type_id: e.target.value,
                  })
                }
                label="Type"
                required
              >
                <MenuItem value={reciprocityTypes.Reciprocity}>
                  Reciprocity
                </MenuItem>
                <MenuItem value={reciprocityTypes.Group}>Group</MenuItem>
              </Select>
            </FormControl>
            <SPDSelector
              spd={createData.spd_chapter_id || ''}
              handleSpdSelect={(spdId) =>
                setCreateData({ ...createData, spd_chapter_id: spdId || '' })
              }
              keyId="create-reciprocity"
            />
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={createData.is_active ? 'true' : 'false'}
                onChange={(e) =>
                  setCreateData({
                    ...createData,
                    is_active: e.target.value === 'true',
                  })
                }
                label="Status"
              >
                <MenuItem value="true">Active</MenuItem>
                <MenuItem value="false">Inactive</MenuItem>
              </Select>
            </FormControl>
            {canEditFinancial && (
              <>
                <TextField
                  fullWidth
                  label="Financial Name"
                  value={createData.financial_name || ''}
                  onChange={(e) =>
                    setCreateData({
                      ...createData,
                      financial_name: e.target.value,
                    })
                  }
                />
                <TextField
                  fullWidth
                  label="Tax ID Number"
                  value={createData.tax_id_number || ''}
                  onChange={(e) =>
                    setCreateData({
                      ...createData,
                      tax_id_number: e.target.value,
                    })
                  }
                />
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => {
              setShowCreateDialog(false);
              setCreateData({
                name: '',
                reciprocity_type_id: '',
                spd_chapter_id: '',
                is_active: true,
                financial_name: undefined,
                tax_id_number: undefined,
              });
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleCreateReciprocity}
            disabled={
              !createData.name ||
              !createData.reciprocity_type_id ||
              !createData.spd_chapter_id ||
              isCreating
            }
          >
            Create Reciprocity
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}
